import {
	Phone,
	MoreVertical,
	UserPlus,
	UserMinus,
	Tag,
	Link,
	ExternalLink,
	Pin,
	PinOff,
	Ban,
	ShieldOff,
	Search,
	ArrowRight,
	CheckCircle,
	Inbox,
	ArrowLeft,
	MailCheck,
	Clock,
	UserX,
	Unlink,
	User,
	Eye,
	EyeOff,
	Gift,
	ListChecks,
} from 'lucide-react'

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { cn } from '@/common/lib/utils'
import { useIsMobile } from '@/common/hooks/useMobile'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/common/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'

import {
	useQueryChatRoom,
	useMutationGetPlayerFullPhone,
	useQueryAdminTasks,
} from '../../../api/hooks'

import { ChatAiModeSelector } from './ChatAiModeSelector'

import { ChatRoomStatus, OperatorGroupPermission } from '@/schema'
import { toast } from 'sonner'
import useQueryGetOperatorMe from '@/modules/operator/api/hooks/useQueryGetMe'
import { useAiGlobalEnabled } from '../../../api/hooks'
interface ChatRoomHeaderProps {
	room: NonNullable<ReturnType<typeof useQueryChatRoom>['data']>
	displayName: string
	onClaim: () => void
	onRelease: () => void
	onOpenTagManager: () => void
	onOpenLinkPlayer: () => void
	onOpenSearch?: () => void
	onPinRoom?: (isPinned: boolean) => void
	onOpenBanDialog?: () => void
	onUnbanRoom?: () => void
	onUpdateStatus?: (status: ChatRoomStatus) => void
	onMarkAsRead?: () => void
	isMyChat?: boolean
	onOpenProfile?: () => void
	onGoToPlayer?: () => void
	onOpenInstantCredit?: () => void
	onToggleAdminTasks?: () => void
}

// Get next status action config based on current status
// INBOX -> FOLLOW_UP -> RESOLVED -> INBOX
function getNextStatusAction(currentStatus: string): {
	nextStatus: ChatRoomStatus
	label: string
	icon: typeof ArrowRight
	className: string
	iconClassName: string
} | null {
	switch (currentStatus) {
		case ChatRoomStatus.INBOX:
			return {
				nextStatus: ChatRoomStatus.FOLLOW_UP,
				label: 'ติดตาม',
				icon: Clock,
				className:
					'border-amber-500 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20',
				iconClassName: 'text-amber-600',
			}
		case ChatRoomStatus.FOLLOW_UP:
			return {
				nextStatus: ChatRoomStatus.RESOLVED,
				label: 'แก้ไขแล้ว',
				icon: CheckCircle,
				className:
					'border-emerald-500 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20',
				iconClassName: 'text-emerald-600',
			}
		case ChatRoomStatus.RESOLVED:
			return {
				nextStatus: ChatRoomStatus.INBOX,
				label: 'อินบ็อค',
				icon: Inbox,
				className:
					'border-blue-500 bg-blue-500/10 text-blue-600 hover:bg-blue-500/20',
				iconClassName: 'text-blue-600',
			}
		default:
			return null
	}
}

export function ChatRoomHeader({
	room,
	displayName,
	onClaim,
	onRelease,
	onOpenTagManager,
	onOpenLinkPlayer,
	onOpenSearch,
	onPinRoom,
	onOpenBanDialog,
	onUnbanRoom,
	onUpdateStatus,
	onMarkAsRead,
	isMyChat,
	onOpenProfile,
	onGoToPlayer,
	onOpenInstantCredit,
	onToggleAdminTasks,
}: ChatRoomHeaderProps) {
	const headerRef = useRef<HTMLDivElement>(null)
	const isMobile = useIsMobile()
	const [isNarrowHeader, setIsNarrowHeader] = useState(false)
	const statusAction = getNextStatusAction(room.status)
	const navigate = useNavigate()

	const { data: operatorMe } = useQueryGetOperatorMe()
	const { data: adminTasks } = useQueryAdminTasks(room.id)
	const { aiEnabled } = useAiGlobalEnabled()
	const pendingTaskCount = adminTasks?.length ?? 0

	// State for full phone toggle
	const [showFullPhone, setShowFullPhone] = useState(false)
	const [fullPhone, setFullPhone] = useState<string | null>(null)
	const getFullPhoneMutation = useMutationGetPlayerFullPhone()

	const hasViewFullPhonePermission =
		operatorMe?.userGroup?.readPermission?.includes(
			OperatorGroupPermission.VIEW_FULL_PHONE,
		) ||
		operatorMe?.userGroup?.writePermission?.includes(
			OperatorGroupPermission.VIEW_FULL_PHONE,
		)
	const isCompactHeader = isMobile || isNarrowHeader

	useEffect(() => {
		const headerElement = headerRef.current
		if (!headerElement) return

		const updateHeaderWidth = () => {
			setIsNarrowHeader(headerElement.clientWidth < 640)
		}

		updateHeaderWidth()

		const resizeObserver = new ResizeObserver(updateHeaderWidth)
		resizeObserver.observe(headerElement)

		return () => resizeObserver.disconnect()
	}, [])

	const handleTogglePhone = async () => {
		if (!room.player?.id) return

		if (showFullPhone) {
			// Hide full phone
			setShowFullPhone(false)
			return
		}

		// Show full phone - fetch if not already fetched
		if (fullPhone) {
			setShowFullPhone(true)
		} else {
			try {
				const result = await getFullPhoneMutation.mutateAsync(room.player.id)
				setFullPhone(result.phone)
				setShowFullPhone(true)
			} catch {
				toast.error('ไม่สามารถดูเบอร์โทรได้')
			}
		}
	}

	return (
		<div ref={headerRef} className="border-b">
			{/* Main header row */}
			<div className="flex min-w-0 items-center justify-between gap-2 px-2 py-2 md:px-4 md:py-3">
				<div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
					{/* Back button for mobile */}
					{isMobile && (
						<Button
							variant="ghost"
							size="icon"
							onClick={() => navigate('/operator/chat')}
							className="h-8 w-8 shrink-0"
						>
							<ArrowLeft className="h-5 w-5" />
						</Button>
					)}
					<Button
						type="button"
						onClick={onOpenProfile}
						variant="ghost"
						size="icon"
						className="rounded-full"
					>
						<Avatar className="h-8 w-8 md:h-10 md:w-10">
							<AvatarImage src={room.linePictureUrl} alt={displayName} />
							<AvatarFallback className="text-xs md:text-sm">
								{displayName.slice(0, 2).toUpperCase()}
							</AvatarFallback>
						</Avatar>
					</Button>
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-1">
							<h3 className="truncate text-sm font-semibold md:text-base">
								{displayName}
							</h3>
						</div>
						<div className="flex min-w-0 items-center gap-1 overflow-hidden text-xs text-muted-foreground md:gap-2 md:text-sm">
							{room.player && (
								<div className="flex min-w-0 items-center gap-1">
									<button
										type="button"
										onClick={onGoToPlayer}
										className="flex min-w-0 items-center gap-1 underline"
									>
										{room.player.username && !showFullPhone ? (
											<>
												<User className="h-3 w-3 shrink-0" />
												<span className="truncate">{room.player.username}</span>
											</>
										) : (
											<>
												<Phone className="h-3 w-3 shrink-0" />
												<span className="truncate">
													{showFullPhone && fullPhone
														? fullPhone
														: room.player.phone}
												</span>
											</>
										)}
									</button>
									{hasViewFullPhonePermission && room.player.username && (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														type="button"
														onClick={handleTogglePhone}
														className="flex items-center hover:text-foreground"
														disabled={getFullPhoneMutation.isPending}
													>
														{showFullPhone ? (
															<EyeOff className="h-3 w-3" />
														) : (
															<Eye className="h-3 w-3" />
														)}
													</button>
												</TooltipTrigger>
												<TooltipContent>
													{showFullPhone
														? 'ซ่อนเบอร์โทรเต็ม'
														: 'แสดงเบอร์โทรเต็ม'}
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
								</div>
							)}
							{room.player && !room.player.lineId && (
								<Badge
									variant="outline"
									className="shrink-0 gap-1 border-orange-500 bg-orange-500/10 text-[10px] text-orange-600 md:text-xs"
								>
									<Unlink className="h-3 w-3 shrink-0" />
									ยังไม่เชื่อม LINE
								</Badge>
							)}
							{room.assignedOperator && (
								<Badge
									variant={isMyChat ? 'default' : 'secondary'}
									className={cn(
										'shrink-0 text-[10px] md:text-xs',
										isMyChat && 'bg-green-600 hover:bg-green-700',
									)}
								>
									{isMyChat ? 'แชทของคุณ' : room.assignedOperator.username}
								</Badge>
							)}
							{room.status === ChatRoomStatus.CLOSED && (
								<Badge
									variant="outline"
									className="shrink-0 gap-1 border-muted-foreground/50 text-[10px] text-muted-foreground md:text-xs"
								>
									<UserX className="h-3 w-3 shrink-0" />
									ยกเลิกติดตาม
								</Badge>
							)}
						</div>
					</div>
				</div>

				<div className="flex shrink-0 items-center gap-1 md:gap-2">
					{/* Quick Claim/Release Button - Always visible */}
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								{room.assignedOperator ? (
									<Button
										variant={isMyChat ? 'default' : 'outline'}
										size={isCompactHeader ? 'icon' : 'sm'}
										onClick={onRelease}
										className={cn(
											isCompactHeader ? 'h-8 w-8' : 'gap-1',
											isMyChat && 'bg-green-600 hover:bg-green-700',
										)}
									>
										<UserMinus className="h-4 w-4" />
										{!isCompactHeader && <span>ปล่อยแชท</span>}
									</Button>
								) : (
									<Button
										variant="default"
										size={isCompactHeader ? 'icon' : 'sm'}
										onClick={onClaim}
										className={isCompactHeader ? 'h-8 w-8' : 'gap-1'}
									>
										<UserPlus className="h-4 w-4" />
										{!isCompactHeader && <span>รับแชท</span>}
									</Button>
								)}
							</TooltipTrigger>
							<TooltipContent>
								{room.assignedOperator ? 'ปล่อยแชทนี้' : 'รับแชทนี้'}
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>

					{/* Instant Credit Button - Only on desktop */}
					{!isCompactHeader && onOpenInstantCredit && (
						<Button
							variant="outline"
							size="sm"
							onClick={onOpenInstantCredit}
							className="gap-1 border-amber-500 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
						>
							<Gift className="h-4 w-4" />
							<span>ส่งเครดิตฟรี</span>
						</Button>
					)}

					{/* Admin Tasks Button - Show pending count */}
					{aiEnabled && onToggleAdminTasks && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="outline"
										size={isCompactHeader ? 'icon' : 'sm'}
										onClick={onToggleAdminTasks}
										className={cn(
											isCompactHeader ? 'relative h-8 w-8' : 'relative gap-1',
											pendingTaskCount > 0 &&
												'border-amber-500 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20',
										)}
									>
										<ListChecks className="h-4 w-4" />
										{!isCompactHeader && <span>งานแอดมิน</span>}
										{pendingTaskCount > 0 && (
											<span
												className={cn(
													'flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white',
													isCompactHeader ? 'absolute -right-1 -top-1' : 'ml-1',
												)}
											>
												{pendingTaskCount}
											</span>
										)}
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									{pendingTaskCount > 0
										? `แจ้งเตือน ${pendingTaskCount} รายการ`
										: 'แจ้งเตือน'}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}

					{/* Status Change Button - Hidden on mobile, moved to dropdown */}
					{!isCompactHeader && onUpdateStatus && statusAction && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										onClick={() => onUpdateStatus(statusAction.nextStatus)}
										className={cn('gap-1', statusAction.className)}
									>
										<statusAction.icon className="h-4 w-4" />
										<span>{statusAction.label}</span>
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									เปลี่ยนสถานะเป็น {statusAction.label}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}

					{/* AI Mode selector — hidden when AI is globally disabled or OFF. */}
					{aiEnabled && !room.isBanned && room.aiAnswerMode !== 'OFF' && (
						<ChatAiModeSelector
							roomId={room.id}
							currentMode={room.aiAnswerMode ?? 'ASSIS'}
						/>
					)}

					{/* Search Button - Hidden on mobile, moved to dropdown */}
					{!isCompactHeader && onOpenSearch && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="ghost" size="icon" onClick={onOpenSearch}>
										<Search className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>ค้นหาข้อความ</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}

					{/* Pin Button - Hidden on mobile, moved to dropdown */}
					{!isCompactHeader && onPinRoom && !room.isBanned && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => onPinRoom(!room.isPinned)}
										className={room.isPinned ? 'text-primary' : ''}
									>
										{room.isPinned ? (
											<PinOff className="h-4 w-4" />
										) : (
											<Pin className="h-4 w-4" />
										)}
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									{room.isPinned ? 'ยกเลิกปักหมุด' : 'ปักหมุดห้อง'}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}

					{/* Unban Button - Show directly when room is banned */}
					{room.isBanned && onUnbanRoom ? (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="destructive"
										size={isCompactHeader ? 'icon' : 'sm'}
										onClick={onUnbanRoom}
										className={isCompactHeader ? 'h-8 w-8' : 'gap-1'}
									>
										<ShieldOff className="h-4 w-4" />
										{!isCompactHeader && <span>ยกเลิกการระงับ</span>}
									</Button>
								</TooltipTrigger>
								<TooltipContent>ยกเลิกการระงับห้องนี้</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					) : (
						/* Actions Dropdown - Only show when room is not banned */
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className={isCompactHeader ? 'h-8 w-8' : ''}
								>
									<MoreVertical className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{isCompactHeader && onOpenInstantCredit && (
									<DropdownMenuItem onClick={onOpenInstantCredit}>
										<Gift className="mr-2 h-4 w-4" />
										ส่งเครดิตฟรี
									</DropdownMenuItem>
								)}

								{/* Compact-only: Status change */}
								{isCompactHeader && onUpdateStatus && statusAction && (
									<>
										<DropdownMenuItem
											onClick={() => onUpdateStatus(statusAction.nextStatus)}
											className={statusAction.iconClassName}
										>
											<statusAction.icon className="mr-2 h-4 w-4" />
											{statusAction.label}
										</DropdownMenuItem>
										<DropdownMenuSeparator />
									</>
								)}

								{/* Compact-only: Search */}
								{isCompactHeader && onOpenSearch && (
									<DropdownMenuItem onClick={onOpenSearch}>
										<Search className="mr-2 h-4 w-4" />
										ค้นหาข้อความ
									</DropdownMenuItem>
								)}

								{onPinRoom && (
									<DropdownMenuItem onClick={() => onPinRoom(!room.isPinned)}>
										{room.isPinned ? (
											<>
												<PinOff className="mr-2 h-4 w-4" />
												ยกเลิกปักหมุด
											</>
										) : (
											<>
												<Pin className="mr-2 h-4 w-4" />
												ปักหมุดห้อง
											</>
										)}
									</DropdownMenuItem>
								)}
								{onMarkAsRead && (
									<DropdownMenuItem
										onClick={onMarkAsRead}
										disabled={room.unreadCount === 0}
									>
										<MailCheck className="mr-2 h-4 w-4" />
										อ่านแล้ว
									</DropdownMenuItem>
								)}
								{onOpenBanDialog && (
									<DropdownMenuItem
										onClick={onOpenBanDialog}
										className="text-destructive focus:text-destructive"
									>
										<Ban className="mr-2 h-4 w-4" />
										ระงับห้อง (สแปม)
									</DropdownMenuItem>
								)}
								{
									<DropdownMenuItem
										onClick={onOpenTagManager}
										disabled={!room.player}
									>
										<Tag className="mr-2 h-4 w-4" />
										จัดการแท็ก
									</DropdownMenuItem>
								}
								<DropdownMenuItem onClick={onOpenLinkPlayer}>
									<Link className="mr-2 h-4 w-4" />
									{room.player ? 'จัดการผู้เล่น' : 'เชื่อมต่อผู้เล่น'}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								{room.player && (
									<DropdownMenuItem asChild>
										<a
											href={`/operator/get-player-list/player-detail/${room.player.id}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											<ExternalLink className="mr-2 h-4 w-4" />
											ดูข้อมูลผู้เล่น
										</a>
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</div>

			{/* Tags row - Shows all tags in a scrollable row */}
			{room.tags.length > 0 && (
				<div className="flex items-center gap-1.5 overflow-x-auto px-2 pb-2 md:px-4 md:pb-3">
					<Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
					<div className="flex gap-1.5">
						{room.tags.map((tag) => (
							<Badge
								key={tag.id}
								variant="outline"
								className="shrink-0 text-xs"
								style={{ borderColor: tag.color, color: tag.color }}
							>
								{tag.name}
							</Badge>
						))}
					</div>
				</div>
			)}
		</div>
	)
}
